import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays, differenceInCalendarWeeks, isSameDay, isSameMonth, isWeekend } from 'date-fns';
import { cn } from '@/shared/lib/utils';
import type { CalendarEvent } from '../calendarModel';
import { isoDate, monthGridRange } from '../calendarModel';
import { layoutWeek } from '../laneLayout';
import { TermBar } from './TermBar';

interface WeekRowProps {
  weekStart: Date;
  month: Date;
  events: readonly CalendarEvent[];
  onEventClick: (termId: string) => void;
  renderDayAction?: (isoDay: string) => ReactNode;
}

function WeekRow({ weekStart, month, events, onEventClick, renderDayAction }: WeekRowProps) {
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)),
    [weekStart]
  );
  const { segments } = useMemo(() => layoutWeek(events, weekStart), [events, weekStart]);
  const today = new Date();

  return (
    <div className="border-phx-border relative border-b last:border-b-0">
      {/* Podklad: pozadí dnů + akce „+“ (pointer events žijí tady) */}
      <div className="absolute inset-0 grid grid-cols-7">
        {days.map((day) => (
          <div
            key={day.getTime()}
            data-testid="calendar-day-cell"
            data-date={isoDate(day)}
            className={cn(
              'group/day border-phx-border relative border-r last:border-r-0',
              !isSameMonth(day, month) && 'bg-phx-surface-2',
              isWeekend(day) && 'bg-phx-surface-3/40'
            )}
          >
            <span
              className={cn(
                'text-phx-xs absolute top-1 left-1.5 tabular-nums',
                isSameMonth(day, month) ? 'text-phx-text-2' : 'text-phx-text-faint',
                isSameDay(day, today) &&
                  'bg-phx-primary text-phx-primary-fg -mt-0.5 -ml-1 rounded-full px-1.5 py-0.5 font-semibold'
              )}
            >
              {day.getDate()}
            </span>
            {renderDayAction && (
              <span className="absolute top-0.5 right-1 opacity-0 transition-opacity group-hover/day:opacity-100">
                {renderDayAction(isoDate(day))}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Obsahový grid: řádek 1 = odsazení pro čísla dnů, řádky 2+ = lanes s pruhy termínů */}
      <div className="pointer-events-none relative grid min-h-22 auto-rows-min grid-cols-7 gap-y-0.5 pb-1.5">
        {days.map((day) => (
          <span key={day.getTime()} className="h-6" aria-hidden="true" />
        ))}
        {segments.map((segment) => (
          <TermBar key={segment.item.termId} segment={segment} onClick={onEventClick} />
        ))}
      </div>
    </div>
  );
}

interface MonthGridProps {
  month: Date;
  events: readonly CalendarEvent[];
  onEventClick: (termId: string) => void;
  renderDayAction?: (isoDay: string) => ReactNode;
}

/**
 * Měsíční grid Po–Ne. Týdny rostou na výšku podle počtu lanes (žádné absolutní pozicování,
 * pruhy jsou grid itemy s `grid-column`/`grid-row`).
 */
export function MonthGrid({ month, events, onEventClick, renderDayAction }: MonthGridProps) {
  const { t } = useTranslation('planner');
  const { gridStart, gridEnd } = monthGridRange(month);
  const weekCount = differenceInCalendarWeeks(gridEnd, gridStart, { weekStartsOn: 1 }) + 1;
  const weekStarts = Array.from({ length: weekCount }, (_, index) => addDays(gridStart, index * 7));

  const dayNames = [
    t('calendar.days.mon'),
    t('calendar.days.tue'),
    t('calendar.days.wed'),
    t('calendar.days.thu'),
    t('calendar.days.fri'),
    t('calendar.days.sat'),
    t('calendar.days.sun'),
  ];

  return (
    <div
      className="border-phx-border rounded-phx-md flex min-h-0 flex-1 flex-col overflow-auto border"
      data-testid="calendar-month-grid"
    >
      <div className="border-phx-border bg-phx-surface-2 grid shrink-0 grid-cols-7 border-b">
        {dayNames.map((name, index) => (
          <span
            key={name}
            className={cn(
              'text-phx-xs text-phx-text-2 px-1.5 py-1 font-semibold tracking-wide uppercase',
              index >= 5 && 'text-phx-text-muted'
            )}
          >
            {name}
          </span>
        ))}
      </div>
      <div className="flex flex-1 flex-col">
        {weekStarts.map((weekStart) => (
          <WeekRow
            key={weekStart.getTime()}
            weekStart={weekStart}
            month={month}
            events={events}
            onEventClick={onEventClick}
            renderDayAction={renderDayAction}
          />
        ))}
      </div>
    </div>
  );
}
